// NOTICE: KAPTION-INTEGRATION
// agent-presence.VISIBILITY.1
//
// Is this tab's presence actually reaching the server?
//
// Under the `enforce_auto_offline` permission an agent is online only while a
// fresh heartbeat sits in Redis, and the heartbeat has exactly one producer:
// `triggerPresenceInterval` in BaseActionCableConnector, delivered over the
// /cable websocket. Two different things break it, and both look identical to
// the agent — nothing on screen changes, HTTP keeps working, conversations keep
// loading — while teammates and auto-assignment already treat them as gone:
//
//   1. The heartbeat stops while the subscription stays open. Measured in
//      production on 2026-09-14: one agent's subscription was alive server-side
//      for stretches of 25, 25, 55, 42 and 52 minutes with presence reporting
//      him offline the whole time. At an 8s cadence, that much silence means
//      the tab was not running the timer at all — frozen or heavily throttled
//      in the background.
//   2. The socket closes. The client keeps calling `perform('update_presence')`
//      and actioncable discards every message.
//
// So the signal here is NOT "is the socket open" — mode 1 happens with the
// socket wide open. It is the server's own acknowledgement: RoomChannel answers
// every heartbeat with a `presence.update` broadcast on this user's private
// pubsub token, so an inbound presence.update IS proof that a beat landed. No
// acknowledgement in PRESENCE_ACK_STALE_AFTER means presence is not being
// refreshed, whatever the cause.
//
// The debounced disconnect events are kept as a second, faster input for mode 2
// — they are the same ones the "Disconnected" banner uses, so the two
// indicators can never contradict each other, and the sub-second blips every
// deploy causes are already filtered out of them.
//
// State lives at module scope on purpose: connection health is global to the
// app, a shared ref keeps every consumer in agreement, and a component that
// mounts *after* the failure (the profile dropdown only renders when opened)
// still reads the correct value.
import { computed, ref } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useMapGetter } from 'dashboard/composables/store';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import wootConstants from 'dashboard/constants/globals';

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const [ONLINE, , OFFLINE] = AVAILABILITY_STATUS_KEYS;

// Heartbeats are 8s apart and the server keeps presence for 90s in production,
// so 45s of silence is well past "a beat was late" and still leaves the agent
// time to act before presence actually lapses.
export const PRESENCE_ACK_STALE_AFTER = 45000;
// How often the watchdog re-reads the clock. A frozen tab runs no timers, so
// this tick is also what notices the gap the moment the tab comes back.
export const PRESENCE_WATCHDOG_INTERVAL = 5000;

const isSocketDown = ref(false);
const isPresenceAckStale = ref(false);
const lastPresenceRefreshAt = ref(null);
// Seeded at boot: if the first acknowledgement never arrives, the agent really
// is invisible and should be told so.
let lastPresenceAckAt = Date.now();

/**
 * The server confirmed a heartbeat. Called from the presence.update handler.
 */
export const notePresenceAck = () => {
  lastPresenceAckAt = Date.now();
  isPresenceAckStale.value = false;
  lastPresenceRefreshAt.value = null;
};

/**
 * Re-read the clock. Exported so the watchdog, the visibility handler and the
 * specs all go through the same path.
 */
export const checkPresenceAck = () => {
  isPresenceAckStale.value =
    Date.now() - lastPresenceAckAt > PRESENCE_ACK_STALE_AFTER;
};

// Started on first use rather than at import time: importing this module must
// have no side effects, and the profile menu mounts with the app anyway.
let watchStarted = false;
const startPresenceWatch = () => {
  if (watchStarted) return;
  watchStarted = true;

  emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, () => {
    isSocketDown.value = true;
  });
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, () => {
    isSocketDown.value = false;
  });
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, () => {
    isSocketDown.value = false;
  });

  setInterval(checkPresenceAck, PRESENCE_WATCHDOG_INTERVAL);
  document?.addEventListener?.('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkPresenceAck();
  });
};

/**
 * Live-presence truth for the signed-in agent.
 *
 * @returns {Object}
 *   isLiveConnectionDown  — presence is not reaching the server
 *   isPresenceEnforced    — this account resolves availability from presence
 *   isLivePresenceLost    — the agent is set available but nothing reaches the server
 *   effectiveAvailability — what everyone else sees, not what the menu is set to
 *   markPresenceRefreshed — record an explicit availability pick (HTTP presence refresh)
 *   lastPresenceRefreshAt — when that last happened, for the "it lapses again" notice
 */
export function useKaptionLivePresence() {
  startPresenceWatch();

  const { canUseFeature } = useFeaturePermissions();
  const currentUserAvailability = useMapGetter('getCurrentUserAvailability');

  // feature-permissions.CONVERSATION.13 — inverted semantics: being permitted
  // the feature means presence enforcement applies to you.
  const isPresenceEnforced = computed(() =>
    canUseFeature('enforce_auto_offline')
  );

  const isLiveConnectionDown = computed(
    () => isSocketDown.value || isPresenceAckStale.value
  );

  // agent-presence.VISIBILITY.2 — an agent who chose Offline is already telling
  // everyone not to route to them; a lost heartbeat changes nothing for them.
  const isLivePresenceLost = computed(
    () =>
      isPresenceEnforced.value &&
      isLiveConnectionDown.value &&
      currentUserAvailability.value !== OFFLINE
  );

  const effectiveAvailability = computed(() =>
    isLivePresenceLost.value ? OFFLINE : currentUserAvailability.value
  );

  const markPresenceRefreshed = () => {
    lastPresenceRefreshAt.value = new Date();
  };

  return {
    ONLINE,
    OFFLINE,
    isSocketDown,
    isPresenceAckStale,
    isLiveConnectionDown,
    isPresenceEnforced,
    isLivePresenceLost,
    effectiveAvailability,
    lastPresenceRefreshAt,
    markPresenceRefreshed,
  };
}
